import { useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import './OrderDetails.scss';

import useResponsive from '../../../hooks/useResponsive';
import { LogoSection } from '../../../Components/LogoSection';
import { OrderSummary } from '../../../Components/OrderSummary';
import { Title, Text } from '../../../Components/Typography';
import { Button } from '../../../Components/Button';
import {
  AdminContext,
  AdminContextProps,
} from '../../../Contexts/AdminContext';
import { isEmpty } from '../../../utils/isEmpty';
import FileCard from '../../../Components/FileCard/FileCard';
import { OrderStatusDropdown } from '../../../Components/Dropdown/OrderStatusDropdown/OrderStatusDropdown';
import { LoadingSpinner } from '../../../Components/Loading';

export const OrderDetails = () => {
  const { isDesktopOrLaptop } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const {
    selectedOrder,
    orderUploads,
    selectedOrderStatus,
    setSelectedOrderStatus,
    updateNewOrderStatus,
    isPaymentApproved,
    TrackingshippingCode,
    downloadOrderZip,
  }: AdminContextProps = useContext<any>(AdminContext);
  useEffect(() => {
    setSelectedOrderStatus(selectedOrder.id_status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const filesCount = Array.isArray(orderUploads) ? orderUploads.length : 0;
  const showDownloadAll = filesCount > 1;

  // arriba del return:
  const stripRef = useRef<HTMLDivElement | null>(null);

  const onChangeStatus = async () => {
    setLoading(true);

    await updateNewOrderStatus();
    setLoading(false);
    toast.success('Estado actualizado exitosamente!');
  };

  const handleDownloadAll = async () => {
    if (!selectedOrder?.tracking_code) {
      toast.warn('La orden no tiene tracking_code.');
      return;
    }
    const id = toast.loading(
      `Generando ZIP de la orden ${selectedOrder.tracking_code}...`
    );
    setDownloadingZip(true);
    try {
      await downloadOrderZip(selectedOrder.tracking_code);
      toast.update(id, {
        render: 'Descarga Exitosa',
        type: 'success',
        isLoading: false,
        autoClose: 2000,
        closeOnClick: true,
      });
    } catch (e: any) {
      toast.update(id, {
        render: e?.message ?? 'Error al descargar el ZIP',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setDownloadingZip(false);
    }
  };

  const updateArrows = () => {
    const el = stripRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 0);
    setCanRight(el.scrollLeft < maxScroll - 1);
  };

  useEffect(() => {
    updateArrows();
    const onResize = () => updateArrows();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [orderUploads]);

  const scrollByAmount = (dir: -1 | 1) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const onTrackScroll: React.UIEventHandler<HTMLDivElement> = () =>
    updateArrows();

  return (
    <div
      className={
        isDesktopOrLaptop ? 'order-details-desktop' : 'order-details-mobile'
      }
    >
      <LogoSection style={{ marginRight: '10px' }} showInstructions={false} />

      {isDesktopOrLaptop && (
        <OrderSummary
          style={{ width: '365px', marginLeft: '180px' }}
          isPreview
          isAdmin
        />
      )}

      <div className="main-column">
        <div className="text-section">
          <div className="block">
            <Text bold>Estado</Text>

            <div className="flex-row" style={{ gap: '20px' }}>
              <OrderStatusDropdown
                selectedStatus={selectedOrderStatus}
                onChange={setSelectedOrderStatus}
                disabled={!isPaymentApproved()}
              />
              {loading ? (
                <LoadingSpinner width={50} height={50} />
              ) : (
                <Button
                  onClick={onChangeStatus}
                  disabled={!isPaymentApproved()}
                >
                  Guardar Cambios
                </Button>
              )}
            </div>
            <br />
            <div className="flex-row" style={{ gap: '20px' }}>
              <Text bold>Estado del pago</Text>
              <Text>{selectedOrder.payment_status}</Text>
            </div>
          </div>

          <div className="block">
            {orderUploads && (
              <>
                <br />
                <div className="flex-column">
                  <div
                    className="flex-row"
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <Title>Archivos</Title>

                    {showDownloadAll && (
                      <Button
                        onClick={handleDownloadAll}
                        disabled={downloadingZip}
                      >
                        {downloadingZip ? 'Descargando...' : 'Descargar Todos'}
                      </Button>
                    )}
                  </div>

                  <br />
                  <div className="files-strip">
                    <button
                      type="button"
                      className="files-arrow left"
                      onClick={() => scrollByAmount(-1)}
                      disabled={!canLeft}
                      aria-label="Desplazar a la izquierda"
                    >
                      ‹
                    </button>

                    <div
                      className="files-track"
                      ref={stripRef}
                      onScroll={onTrackScroll}
                    >
                      {orderUploads.map((file: any) => (
                        <div key={file.id} className="file-item">
                          <FileCard
                            name={file.name}
                            url={file.url}
                            size={file.size}
                          />
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="files-arrow right"
                      onClick={() => scrollByAmount(1)}
                      disabled={!canRight}
                      aria-label="Desplazar a la derecha"
                    >
                      ›
                    </button>
                    <div
                      className={`edge-fade left ${canLeft ? 'show' : ''}`}
                    />
                    <div
                      className={`edge-fade right ${canRight ? 'show' : ''}`}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="block">
            <Title>Datos del Pedido</Title>

            <div
              className="flex-row-top"
              style={{ gap: '50px', marginTop: '20px' }}
            >
              <div className="flex-column">
                <Text bold>{selectedOrder.user_data?.name}</Text>
                <Text>{selectedOrder.user_data?.dni}</Text>
                <Text>{selectedOrder.user_data?.email}</Text>
                <Text>{selectedOrder.user_data?.phoneNumber}</Text>
              </div>

              {!isEmpty(selectedOrder.sucursal) ? (
                <div className="flex-column">
                  <Text bold>Retiro por local</Text>
                  <Text>{selectedOrder.sucursal}</Text>
                  <Text>{selectedOrder.sucursal_address}</Text>
                </div>
              ) : (
                <>
                  <div className="flex-column" style={{ width: '150px' }}>
                    <Text bold>Envío</Text>
                    <Text>{selectedOrder.address_send?.address}</Text>
                    <Text>{selectedOrder.address_send?.addressDetails}</Text>
                    <Text>{selectedOrder.address_send?.location}</Text>
                    <Text>{selectedOrder.address_send?.province.text}</Text>
                    <Text>{selectedOrder.address_send?.postalCode}</Text>
                  </div>
                  <div className="flex-column" style={{ width: '250px' }}>
                    <Text bold>Notas envío a domicilio</Text>
                    <Text>{selectedOrder.address_send?.additionalNotes}</Text>
                  </div>
                </>
              )}
            </div>
            <div>
              {selectedOrder.shipping_code &&
                (selectedOrder.id_status === 3 ||
                  selectedOrder.id_status === 4) && (
                  <div style={{ marginBottom: '20px' }}>
                    <div
                      style={{ marginTop: '30px', cursor: 'pointer' }}
                      onClick={() =>
                        TrackingshippingCode(1, selectedOrder.shipping_code)
                      }
                    >
                      <p>
                        <a className="hyperlink">
                          Consulta aquí el seguimiento del envío.
                        </a>
                      </p>
                    </div>

                    <div
                      style={{ marginTop: '20px', cursor: 'pointer' }}
                      onClick={() =>
                        TrackingshippingCode(2, selectedOrder.shipping_code)
                      }
                    >
                      <p>
                        <a className="hyperlink">Etiqueta de pedido.</a>
                      </p>
                    </div>
                  </div>
                )}
            </div>
            <div className="block">
              <div className="flex-column">
                <Text bold>Notas Adicionales</Text>
                <Text>{selectedOrder.notes}</Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
